import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaEdit } from "react-icons/fa";
import Cookies from "js-cookie";

const API = process.env.REACT_APP_API_BASE_URL;

interface Affiliate {
  id: number;
  name: string;
  email: string;
  phone: string;
  affiliate_code: string;
  status: string;
  total_orders: number;
  total_revenue: number;
  total_commission: number;
}

export default function UsersAffiliatePage() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAffiliates = async () => {
    try {
      const token = Cookies.get("token");

      const res = await axios.get(`${API}/api/admin/affiliates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page,
        },
      });

      setAffiliates(res.data.data);
      setTotalPages(res.data.last_page);
    } catch {
      Swal.fire("Error", "Gagal mengambil data affiliate", "error");
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, [page]);

  const openEdit = (affiliate: Affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selectedAffiliate) return;

    const token = Cookies.get("token");

    try {
      await axios.put(
        `${API}/api/admin/affiliate/${selectedAffiliate.id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      Swal.fire("Berhasil", "Affiliate berhasil di approve", "success");

      setShowModal(false);
      fetchAffiliates();
    } catch {
      Swal.fire("Error", "Gagal approve affiliate", "error");
    }
  };

  return (
    <div className="mt-7">
      {/* HEADER */}
      <h1 className="text-2xl font-bold mb-6">Affiliate Management</h1>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Nama</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Affiliate Code</th>
              <th className="p-3 text-left">Status</th>

              <th className="p-3 text-center">Aksi</th>
            </tr>
          </thead>

          <tbody>
            {affiliates.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="p-3">{a.name}</td>
                <td className="p-3">{a.email}</td>
                <td className="p-3">{a.phone}</td>
                <td className="p-3 font-mono">{a.affiliate_code}</td>

                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs ${a.status === "approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{a.status}</span>
                </td>

                <td className="p-3 flex justify-center">
                  <button onClick={() => openEdit(a)} className="text-blue-500 hover:text-blue-700">
                    <FaEdit />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-end gap-2 mt-4">
        {[...Array(totalPages)].map((_, i) => (
          <button key={i} onClick={() => setPage(i + 1)} className={`px-3 py-1 rounded ${page === i + 1 ? "bg-primary text-white" : "bg-gray-200"}`}>
            {i + 1}
          </button>
        ))}
      </div>

      {/* MODAL APPROVE */}
      {showModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-[400px]">
            <h2 className="text-xl font-bold mb-4">Approve Affiliate</h2>

            <div className="space-y-2 text-sm">
              <p>
                <b>Nama :</b> {selectedAffiliate.name}
              </p>
              <p>
                <b>Email :</b> {selectedAffiliate.email}
              </p>
              <p>
                <b>Phone :</b> {selectedAffiliate.phone}
              </p>
              <p>
                <b>Affiliate Code :</b> {selectedAffiliate.affiliate_code}
              </p>
              <p>
                <b>Orders :</b> {selectedAffiliate.total_orders}
              </p>
              <p>
                <b>Revenue :</b> Rp. {new Intl.NumberFormat("id-ID").format(selectedAffiliate.total_revenue)}
              </p>
              <p>
                <b>Commission :</b> Rp. {new Intl.NumberFormat("id-ID").format(selectedAffiliate.total_commission)}
              </p>
              <p>
                <b>Status :</b> {selectedAffiliate.status}
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="w-full bg-gray-200 py-2 rounded">
                Tutup
              </button>

              {selectedAffiliate.status !== "approved" && (
                <button onClick={handleApprove} className="w-full bg-green-500 text-white py-2 rounded">
                  Approve
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
